"use client";

import { Box, Link, styled } from "@mui/material";
import { useEffect } from "react";
import Quantum from "@components/shared/loading/QuantumLoader";
import { useOnboarding, ONBOARDING_STATUS } from "@/lib/onboarding/OnboardingContext";

/**
 * Top-of-screen onboarding bar. Reuses the CV import status bar's styled shell
 * (same 42px slide-down) but is driven by the onboarding state machine — a
 * progress message while researching, and a review CTA once ready.
 */
const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  height: active ? "42px" : "0px",
  width: "100%",
  top: "0",
  left: "0",
  position: "fixed",
  overflow: "hidden",
  zIndex: theme.zIndex.drawer + 5,
  transition: "height 0.8s ease",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#ddeafc",
}));

const Content = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  columnGap: "16px",
  height: "100%",
  fontWeight: "600",
  fontSize: "0.8rem",
});

const Links = styled(Box)({
  display: "flex",
  columnGap: "8px",
});

export default function OnboardingStatusBar({ open, onOpen }) {
  const { status, message, barVisible, openModal, openEmailPrompt, runAgain } =
    useOnboarding();

  useEffect(() => {
    onOpen(barVisible);
  }, [barVisible, onOpen]);

  const needsEmail = status === ONBOARDING_STATUS.NEEDS_EMAIL;
  const researching = status === ONBOARDING_STATUS.RESEARCHING;
  const ready = status === ONBOARDING_STATUS.READY;
  const errored = status === ONBOARDING_STATUS.ERROR;

  const text = needsEmail
    ? "Add your work email so we can finish setting up your account."
    : researching
      ? message || "Setting up your account for you..."
      : ready
        ? "We've configured your account from your details."
        : errored
          ? "We couldn't finish setting up your account."
          : "";

  const handleAddEmail = (e) => {
    e.preventDefault();
    openEmailPrompt();
  };

  const handleReview = (e) => {
    e.preventDefault();
    openModal();
  };

  const handleRetry = (e) => {
    e.preventDefault();
    runAgain();
  };

  return (
    <Container active={open}>
      <Content>
        {researching && <Quantum />}
        <p>{text}</p>
        {needsEmail && (
          <Links>
            <Link href="#" onClick={handleAddEmail}>
              Add work email
            </Link>
          </Links>
        )}
        {ready && (
          <Links>
            <Link href="#" onClick={handleReview}>
              Review onboarding details
            </Link>
          </Links>
        )}
        {errored && (
          <Links>
            <Link href="#" onClick={handleRetry}>
              Try again
            </Link>
          </Links>
        )}
        {researching && <Quantum />}
      </Content>
    </Container>
  );
}
